"use client"
import { useDispatch } from "react-redux"
import { loginStudent, loginAdmin, loginFaculty } from "../store/slices/authSlice"
import { useState } from "react"
import { Box, Button, Container, Paper, Tab, Tabs, TextField, Typography, CircularProgress } from "@mui/material"

// Define the new primary color for a sophisticated look
const PRIMARY_INDIGO = '#1a237e'; // Deep Indigo (primary color)

const AuthComponent = () => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState("student")
  const [formData, setFormData] = useState({ email: "", password: "" })
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  // Switch tabs & reset form
  const handleTabChange = (e, tab) => {
    setActiveTab(tab)
    setFormData({ email: "", password: "" })
    setError("")
  }

  // Input change
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError("")
  }

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")

    try {
      if (activeTab === "admin") {
        console.log("Admin login:", formData)
        await dispatch(loginAdmin(formData)).unwrap()
      } else if (activeTab === "faculty") {
        console.log("Faculty login:", formData)
        await dispatch(loginFaculty(formData)).unwrap()
      } else {
        console.log("Student login:", formData)
        await dispatch(loginStudent(formData)).unwrap()
      }
      // Optionally reset form on success
      setFormData({ email: "", password: "" })
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.")
      console.error("Login error:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const getTabLabel = (tab) => {
    switch (tab) {
      case "admin":
        return "Admin"
      case "faculty":
        return "Faculty"
      case "student":
        return "Student"
      default:
        return "Student"
    }
  }

  return (
    <Container
      maxWidth="xs"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Classy background: subtle radial light blue/grey for depth
        background: 'radial-gradient(circle at top left, #ffffff 0%, #f0f4f8 70%)', 
        fontFamily: 'Inter, sans-serif', // Use a modern sans-serif font stack
      }}
    >
      <Paper
        elevation={12} // Increased elevation for a floating, premium look
        sx={{
          p: { xs: 3, sm: 5 }, // Responsive padding
          width: "100%",
          maxWidth: 400, // Constrain max width for cleaner appearance
          // Subtle border and increased radius
          border: `1px solid ${PRIMARY_INDIGO}10`,
          borderRadius: 4, 
          bgcolor: "#fafafa", // Slightly off-white background for texture
          transition: 'transform 0.3s, box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: `0px 15px 35px ${PRIMARY_INDIGO}40`, // Deeper shadow on hover
            transform: 'translateY(-2px)', // Subtle lift on hover
          }
        }}
      >
       {/* Header - Custom Sized Typography */}
<Typography
  align="center"
  sx={{
    fontWeight: 700,
    color: PRIMARY_INDIGO,
    letterSpacing: '-0.01em',
    mb: 0.5,
    fontSize: '1.75rem', // Between h4 (~1.8rem) and h5 (~1.5rem)
  }}
>
  FACULTY PORTAL
</Typography>

<Typography
  align="center"
  sx={{
    color: PRIMARY_INDIGO,
    opacity: 0.8,
    fontWeight: 400,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: '0.8rem', // Slightly bigger than h6
    marginBottom: '10px'
  }}
>
  IMS – Institute Management System
</Typography>

        {/* Tab Switcher */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            mb: 3,
            borderBottom: `2px solid #e0e0e0`,
            "& .MuiTabs-indicator": {
              backgroundColor: PRIMARY_INDIGO, 
              height: 3,
            },
          }}
        >
          <Tab
            label="Student"
            value="student"
            sx={{
              textTransform: "uppercase",
              fontWeight: activeTab === 'student' ? 700 : 500,
              color: activeTab === 'student' ? PRIMARY_INDIGO : '#757575',
              "&:hover": { color: PRIMARY_INDIGO, opacity: 0.8 },
            }}
          />
          <Tab
            label="Faculty"
            value="faculty"
            sx={{
              textTransform: "uppercase",
              fontWeight: activeTab === 'faculty' ? 700 : 500,
              color: activeTab === 'faculty' ? PRIMARY_INDIGO : '#757575',
              "&:hover": { color: PRIMARY_INDIGO, opacity: 0.8 },
            }}
          />
          <Tab
            label="Admin"
            value="admin"
            sx={{
              textTransform: "uppercase",
              fontWeight: activeTab === 'admin' ? 700 : 500,
              color: activeTab === 'admin' ? PRIMARY_INDIGO : '#757575',
              "&:hover": { color: PRIMARY_INDIGO, opacity: 0.8 },
            }}
          />
        </Tabs>

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            margin="normal"
            id="email"
            name="email"
            label={`${getTabLabel(activeTab)} Email`}
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            error={!!error}
            variant="outlined"
            size="medium"
            sx={{
              mt: 1, // Added spacing
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                backgroundColor: '#fff', // Ensure fields are white for contrast
                "&.Mui-focused fieldset": {
                  borderColor: PRIMARY_INDIGO,
                  borderWidth: '2px',
                },
              },
              "& .MuiInputLabel-root": {
                color: '#9e9e9e',
                "&.Mui-focused": { color: PRIMARY_INDIGO },
              },
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            id="password"
            name="password"
            label={`${getTabLabel(activeTab)} Password`}
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            error={!!error}
            variant="outlined"
            size="medium"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                backgroundColor: '#fff', // Ensure fields are white for contrast
                "&.Mui-focused fieldset": {
                  borderColor: PRIMARY_INDIGO,
                  borderWidth: '2px',
                },
              },
              "& .MuiInputLabel-root": {
                color: '#9e9e9e',
                "&.Mui-focused": { color: PRIMARY_INDIGO },
              },
            }}
          />

          {/* Error Message */}
          {error && (
            <Typography
              variant="body2"
              sx={{
                color: "#d32f2f",
                mt: 2,
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              mt: 4, // Increased top margin for better separation
              py: 1.75, // Taller, more prominent button
              textTransform: "uppercase",
              letterSpacing: '0.08em', // More pronounced letter spacing
              fontWeight: 700,
              borderRadius: 1.5,
              bgcolor: PRIMARY_INDIGO,
              boxShadow: `0px 6px 20px ${PRIMARY_INDIGO}50`, // Stronger shadow
              "&:hover": {
                bgcolor: '#303f9f', // Slightly lighter indigo on hover
                boxShadow: `0px 8px 25px ${PRIMARY_INDIGO}80`, // Even stronger shadow
                transform: 'translateY(-1px)', // Subtle lift on hover
              },
              transition: 'all 0.3s ease',
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : `Sign In as ${getTabLabel(activeTab)}`}
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default AuthComponent